/**
 * List of international country dial codes with flag icons and names.
 * Default: India (+91) listed first.
 */
export const COUNTRY_CODES = [
  { code: '+91', country: 'IN', label: '🇮🇳 +91 (India)' },
  { code: '+1', country: 'US', label: '🇺🇸 +1 (US / Canada)' },
  { code: '+44', country: 'GB', label: '🇬🇧 +44 (UK)' },
  { code: '+971', country: 'AE', label: '🇦🇪 +971 (UAE)' },
  { code: '+966', country: 'SA', label: '🇸🇦 +966 (Saudi Arabia)' },
  { code: '+974', country: 'QA', label: '🇶🇦 +974 (Qatar)' },
  { code: '+968', country: 'OM', label: '🇴🇲 +968 (Oman)' },
  { code: '+965', country: 'KW', label: '🇰🇼 +965 (Kuwait)' },
  { code: '+973', country: 'BH', label: '🇧🇭 +973 (Bahrain)' },
  { code: '+65', country: 'SG', label: '🇸🇬 +65 (Singapore)' },
  { code: '+60', country: 'MY', label: '🇲🇾 +60 (Malaysia)' },
  { code: '+61', country: 'AU', label: '🇦🇺 +61 (Australia)' },
  { code: '+49', country: 'DE', label: '🇩🇪 +49 (Germany)' },
  { code: '+33', country: 'FR', label: '🇫🇷 +33 (France)' },
  { code: '+39', country: 'IT', label: '🇮🇹 +39 (Italy)' },
  { code: '+34', country: 'ES', label: '🇪🇸 +34 (Spain)' },
  { code: '+31', country: 'NL', label: '🇳🇱 +31 (Netherlands)' },
  { code: '+64', country: 'NZ', label: '🇳🇿 +64 (New Zealand)' },
  { code: '+27', country: 'ZA', label: '🇿🇦 +27 (South Africa)' },
  { code: '+234', country: 'NG', label: '🇳🇬 +234 (Nigeria)' },
  { code: '+254', country: 'KE', label: '🇰🇪 +254 (Kenya)' },
  { code: '+92', country: 'PK', label: '🇵🇰 +92 (Pakistan)' },
  { code: '+880', country: 'BD', label: '🇧🇩 +880 (Bangladesh)' },
  { code: '+94', country: 'LK', label: '🇱🇰 +94 (Sri Lanka)' },
  { code: '+977', country: 'NP', label: '🇳🇵 +977 (Nepal)' },
];

/**
 * Clean & sanitize phone number input by removing spaces, hyphens, and parentheses.
 * @param {string} rawPhone 
 * @returns {string}
 */
export function sanitizePhone(rawPhone) {
  if (!rawPhone) return '';
  return rawPhone.trim().replace(/[\s\-\(\)]/g, '');
}

/**
 * Validate E.164 format and reject obvious dummy repeated numbers.
 * @param {string} fullPhone - Full E.164 phone string (e.g. "+919876543210")
 * @returns {{ valid: boolean, error?: string, sanitized?: string }}
 */
export function validateE164Phone(fullPhone) {
  const sanitized = sanitizePhone(fullPhone);
  if (!sanitized) {
    return { valid: false, error: 'Phone number is required.' };
  }

  // Must start with + and have 7 to 15 digits total
  const e164Regex = /^\+[1-9]\d{6,14}$/;
  if (!e164Regex.test(sanitized)) {
    return { 
      valid: false, 
      error: 'Invalid international phone number format (e.g. +919876543210).' 
    };
  }

  // Check for dummy repeated sequence (e.g. +919999999999 or +910000000000)
  const digitsOnly = sanitized.substring(1); // remove +
  const localPart = digitsOnly.replace(/^[0-9]{1,3}/, ''); // remove country dial digits
  
  if (localPart && /^(\d)\1+$/.test(localPart)) {
    return {
      valid: false,
      error: 'Invalid phone number. Please enter a valid number.'
    };
  }

  return { valid: true, sanitized };
}

/**
 * Parse full E.164 number into countryCode and localNumber.
 * Defaults to countryCode = '+91' if not matching.
 * @param {string} fullPhone 
 * @returns {{ countryCode: string, localNumber: string }}
 */
export function parsePhoneAndCountry(fullPhone) {
  const sanitized = sanitizePhone(fullPhone);
  if (!sanitized || !sanitized.startsWith('+')) {
    return { countryCode: '+91', localNumber: sanitized || '' };
  }

  // Try matching known country codes longest first
  const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const item of sortedCodes) {
    if (sanitized.startsWith(item.code)) {
      return {
        countryCode: item.code,
        localNumber: sanitized.substring(item.code.length)
      };
    }
  }

  return { countryCode: '+91', localNumber: sanitized.substring(1) };
}
